import * as React from "react";
import isArray from "lodash/isArray";
import { reaction } from "mobx";
import { observer } from "mobx-react";

import { renderSpriteFrames } from "util/spritemap-utils";
import { renderWaveform } from "util/audio-utils";
import { renderCues } from "util/caption-utils";
import { editorSectionOutline } from "style/colors";
import { useOnResize } from "util/react-utils";
import Constants from "const/constants";

import { WaveSpinner } from "pages/common/components/state/spinner/spinner";

import {
    SectionLoadingOverlay,
    SectionVisualizationDisplay,
    SectionDisplayThumbnail
} from "./section.styles";

const getCanvasPlaybackStartTimeSeconds = (
    sectionPlaybackStartTime,
    sectionDuration,
    canvasesLength,
    canvasIndex
) => {
    return (
        sectionPlaybackStartTime / 1000 +
        (sectionDuration / canvasesLength) * canvasIndex
    );
};

/**
 * @typedef {SectionVisualizationProps}
 * @property {SectionStore} section
 */

/**
 * @summary Handles loading and rendering of a section's visualization. This can be a thumbnail, spritemap/waveform, or caption section cues. If none of these are ready, a loading animation is shown.
 * @note Rendering is a fairly involved approach, check the comments on `setupCanvases` for more info
 */
const SectionVisualization = (props) => {
    const { section } = props;

    const expectsSpritemap =
        section.asset && section.asset.numberOfVideoStreams > 0;

    const expectsWaveform =
        section.asset && section.asset.numberOfAudioStreams > 0;

    const thumbnailURI = section.asset && section.asset.thumbnailURI;
    const spritemapMetadata = section.asset && section.asset.spritemapMetadata;

    const [spritemapRendered, setSpritemapRendered] = React.useState(false);
    const [waveformRendered, setWaveformRendered] = React.useState(false);
    const [trackRendered, setTrackrendered] = React.useState(false);
    const ref = React.useRef();

    const visualisationsReady = () =>
        (expectsSpritemap && spritemapRendered) ||
        (expectsWaveform && waveformRendered);

    const thumbnailReady = () => !!thumbnailURI;

    const showLoading = () => {
        if (section.asset.type === Constants.MEDIA_TYPE.IMAGE) {
            return !thumbnailReady();
        }
        if (section.asset.type === Constants.MEDIA_TYPE.VOD) {
            return !thumbnailReady() && !visualisationsReady();
        }
    };

    /**
     * This function sets up a row of canvases side by side within a section.
     * This was done to get around a browser limitation that prevented rendering on canvases above a certain size.
     * The visualization is spread over multiple smaller canvases instead.
     */
    const setupCanvases = () => {
        const canvasArrayLength = Math.ceil(ref.current.width / 32000); // TODO - use actual browser max widths
        if (!isArray(ref.current.canvases)) {
            ref.current.canvases = [];
        }

        // Add extra canvases for sections with growing width
        if (canvasArrayLength > ref.current.canvases.length) {
            const amount = canvasArrayLength - ref.current.canvases.length;
            for (let i = 0; i < amount; i++) {
                const canvas = document.createElement(`canvas`);
                canvas.setAttribute(
                    `style`,
                    `position: absolute; top: 0; left: 0;`
                );
                ref.current.canvases.push({
                    el: ref.current.appendChild(canvas)
                });
            }
        }
        // Remove canvases for sections that are shrinking
        if (canvasArrayLength < ref.current.canvases.length) {
            const amount = ref.current.canvases.length - canvasArrayLength;
            for (let i = 0; i < amount; i++) {
                ref.current.canvases.pop().el.remove();
            }
        }

        ref.current.canvases.forEach((canvas, i) => {
            canvas.el.height = ref.current.height;
            canvas.el.width = ref.current.width / ref.current.canvases.length;
            canvas.el.style.left = `${canvas.width * i}px`;
            canvas.inPoint = getCanvasPlaybackStartTimeSeconds(
                section.playbackStartTime,
                section.durationSeconds,
                ref.current.canvases.length,
                i
            );
            canvas.outPoint = getCanvasPlaybackStartTimeSeconds(
                section.playbackStartTime,
                section.durationSeconds,
                ref.current.canvases.length,
                i + 1
            );
        });
    };

    const drawWaveform = () => {
        if (!section.asset.waveformCache) {
            return;
        }
        const height = ref.current.height;
        const width = ref.current.width;

        ref.current.canvases.forEach((canvas) => {
            renderWaveform({
                height: expectsSpritemap ? height * 0.25 : height,
                width,
                x: 0,
                y: expectsSpritemap ? height * 0.75 : 0,
                waveform: section.asset.waveformCache,
                canvas: canvas.el,
                inPoint: canvas.inPoint,
                outPoint: canvas.outPoint
            });
        });

        if (!waveformRendered) {
            setWaveformRendered(true);
        }
    };

    const drawSpritemap = () => {
        if (!section.asset.spritemapCache) {
            return;
        }
        const height = ref.current.height;
        const width = ref.current.width;
        ref.current.canvases.forEach((canvas) => {
            renderSpriteFrames({
                height: expectsWaveform ? height * 0.75 : height,
                width,
                x: 0,
                y: 0,
                canvas: canvas.el,
                sprite: section.asset.spritemapCache,
                from: canvas.inPoint,
                to: canvas.outPoint,
                ...(spritemapMetadata && {
                    frameHeight: spritemapMetadata.height,
                    frameWidth: spritemapMetadata.width,
                    frameInterval: spritemapMetadata.frameIntervalSeconds,
                    framesPerLine: spritemapMetadata.columns
                })
            });
        });

        if (!spritemapRendered) {
            setSpritemapRendered(true);
        }
    };

    const drawCues = () => {
        if (!section.visualizationCues) {
            return;
        }
        const height = ref.current.height;
        const width = ref.current.width;

        ref.current.canvases.forEach((canvas) => {
            renderCues({
                canvas: canvas.el,
                height,
                width,
                x: 0,
                y: 0,
                cues: section.visualizationCues,
                inPoint: canvas.inPoint,
                outPoint: canvas.outPoint,
                playbackStartTime: section.playbackStartTime
            });
        });

        if (!trackRendered) {
            setTrackrendered(true);
        }
    };

    const drawMedia = () => {
        drawWaveform();
        drawSpritemap();
    };

    useOnResize(ref, ({ width, height }) => {
        ref.current.width = width;
        ref.current.height = height;
        setupCanvases();
        drawMedia();
        drawCues();
    });

    React.useEffect(() => {
        const r1 = reaction(() => section.asset.spritemapCache, drawMedia);
        const r2 = reaction(() => section.asset.waveformCache, drawMedia);
        const r3 = reaction(() => section.visualizationCues, drawCues);
        return () => {
            r1();
            r2();
            r3();
        };
    }, []);
    return (
        <SectionVisualizationDisplay>
            {showLoading() && (
                <SectionLoadingOverlay>
                    <WaveSpinner color={editorSectionOutline} />
                </SectionLoadingOverlay>
            )}
            {thumbnailReady() && !visualisationsReady() && (
                <SectionDisplayThumbnail url={thumbnailURI} />
            )}
            <SectionVisualizationDisplay ref={ref}>
                {/* Visualizations canvas to be rendered here */}
            </SectionVisualizationDisplay>
        </SectionVisualizationDisplay>
    );
};

export default observer(SectionVisualization);
